// src/components/DietRecommendation.tsx
import React, { useState, useEffect } from 'react';
import { fetchDietPlan } from '../../services/api';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormHelperText,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  SelectChangeEvent
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface UserData {
  gender: string;
  age: string;
  height: string;
  weight: string;
  bmi: string;
}

interface DietPlanResponse {
  dailyMeals: {
    breakfast: string[];
    lunch: string[];
    dinner: string[];
    snacks: string[];
  };
  nutritionInfo: {
    calories: string;
    protein: string;
    carbs: string;
    fats: string;
  };
  recommendations: string[];
  considerations: string[];
}

const DietRecommendation: React.FC = () => {
  const [userData, setUserData] = useState<UserData>({
    gender: '',
    age: '',
    height: '',
    weight: '',
    bmi: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [medicalReport, setMedicalReport] = useState<File | null>(null);
  const [medicalReportName, setMedicalReportName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [dietPlan, setDietPlan] = useState<string | null>(null);
  const [dietPlanData, setDietPlanData] = useState<DietPlanResponse | null>(null);
  const [showAiInsights, setShowAiInsights] = useState<boolean>(false);

  // Calculate BMI whenever height or weight changes
  useEffect(() => {
    const { height, weight } = userData;

    if (height && weight) {
      try {
        const heightInMeters = parseFloat(height) / 100; // convert cm to meters
        const weightInKg = parseFloat(weight);

        if (!isNaN(heightInMeters) && !isNaN(weightInKg) && heightInMeters > 0) {
          const bmiValue = (weightInKg / (heightInMeters * heightInMeters)).toFixed(1);
          setUserData(prev => ({ ...prev, bmi: bmiValue }));
        }
      } catch (e) {
        // Handle any parsing errors silently
      }
    }
  }, [userData.height, userData.weight]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setUserData(prev => ({ ...prev, [name]: value }));

      // Clear any error for this field
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setMedicalReport(file);
      setMedicalReportName(file.name);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!userData.gender) newErrors.gender = 'Gender is required';
    if (!userData.age) newErrors.age = 'Age is required';
    else if (isNaN(Number(userData.age)) || Number(userData.age) <= 0 || Number(userData.age) > 120)
      newErrors.age = 'Please enter a valid age';

    if (!userData.height) newErrors.height = 'Height is required';
    else if (isNaN(Number(userData.height)) || Number(userData.height) <= 0 || Number(userData.height) > 300)
      newErrors.height = 'Please enter a valid height in cm';

    if (!userData.weight) newErrors.weight = 'Weight is required';
    else if (isNaN(Number(userData.weight)) || Number(userData.weight) <= 0 || Number(userData.weight) > 500)
      newErrors.weight = 'Please enter a valid weight in kg';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      // Prepare form data with all parameters
      const formData = new FormData();
      formData.append('gender', userData.gender);
      formData.append('age', userData.age);
      formData.append('height', userData.height);
      formData.append('weight', userData.weight);
      formData.append('bmi', userData.bmi);

      // Add medical report if available
      if (medicalReport) {
        formData.append('medicalReport', medicalReport);
      }

      // Simulate API delay
      setTimeout(async () => {
        try {
          // For now, we're still using the original API but in production we'd use an enhanced version
          const plan = await fetchDietPlan(userData.bmi);
          setDietPlan(plan);

          // Simulate AI-generated structured diet plan
          generateAIDietPlan(userData, medicalReport !== null);

          setLoading(false);
        } catch (error) {
          console.error('Error fetching diet plan:', error);
          setLoading(false);
        }
      }, 2000); // Simulate 2-second processing time

    } catch (error) {
      console.error('Error submitting form:', error);
      setLoading(false);
    }
  };

  // This function simulates AI-generated diet plans based on user data
  const generateAIDietPlan = (userData: UserData, hasMedicalReport: boolean) => {
    const numericBMI = parseFloat(userData.bmi);
    const age = parseInt(userData.age);

    // Base calories based on gender and age
    let baseCalories = userData.gender === 'male' ? '2500' : '2000';
    if (age > 50) baseCalories = (parseInt(baseCalories) - 200).toString();
    if (age < 25) baseCalories = (parseInt(baseCalories) + 200).toString();

    // Adjust calories based on BMI
    let calorieAdjustment = 0;
    let proteinPercentage = '25%';
    let carbsPercentage = '50%';
    let fatsPercentage = '25%';

    if (numericBMI < 18.5) {
      calorieAdjustment = 300; // Need more calories for underweight
      proteinPercentage = '30%';
      carbsPercentage = '50%';
      fatsPercentage = '20%';
    } else if (numericBMI >= 25) {
      calorieAdjustment = -300; // Need fewer calories for overweight
      proteinPercentage = '35%';
      carbsPercentage = '40%';
      fatsPercentage = '25%';
    }

    const adjustedCalories = (parseInt(baseCalories) + calorieAdjustment).toString();

    // Generate meal recommendations
    let breakfast = ['Oatmeal with berries', 'Greek yogurt with nuts', 'Whole grain toast with avocado'];
    let lunch = ['Quinoa salad with vegetables', 'Grilled chicken with brown rice', 'Lentil soup with whole grain bread'];
    let dinner = ['Baked fish with steamed vegetables', 'Tofu stir-fry with brown rice', 'Lean protein with roasted vegetables'];
    let snacks = ['Apple with almond butter', 'Carrot sticks with hummus', 'Greek yogurt with honey'];

    // Adjust recommendations based on BMI
    if (numericBMI < 18.5) {
      breakfast = ['Protein smoothie with banana and peanut butter', 'Eggs with whole grain toast and avocado', 'Oatmeal with nuts, seeds, and dried fruits'];
      lunch = ['Chicken wrap with avocado and cheese', 'Tuna sandwich with whole grain bread', 'Pasta with olive oil and grilled chicken'];
      dinner = ['Salmon with sweet potato and vegetables', 'Steak with quinoa and roasted vegetables', 'Chicken curry with rice'];
      snacks = ['Trail mix with nuts and dried fruits', 'Protein bar', 'Peanut butter with banana'];
    } else if (numericBMI >= 30) {
      breakfast = ['Egg white omelet with vegetables', 'Greek yogurt with berries', 'Overnight oats with chia seeds'];
      lunch = ['Large salad with grilled chicken', 'Vegetable soup with lean protein', 'Lettuce wraps with turkey'];
      dinner = ['Grilled fish with steamed vegetables', 'Vegetable stir-fry with tofu', 'Zucchini noodles with turkey meatballs'];
      snacks = ['Celery with hummus', 'Air-popped popcorn', 'Cucumber slices'];
    }

    // Generate health considerations
    const considerations = [];
    if (hasMedicalReport) {
      considerations.push('Based on your medical report, this plan is tailored to your specific health needs');
      considerations.push('Regular monitoring of your health metrics is recommended');
    }

    if (numericBMI < 18.5) {
      considerations.push('Focus on nutrient-dense foods to gain weight healthily');
      considerations.push('Consider consulting with a healthcare provider about potential nutrient deficiencies');
    } else if (numericBMI >= 30) {
      considerations.push('Gradual, sustainable weight loss of 0.5-1 kg per week is recommended');
      considerations.push('Consider consulting with a healthcare provider before starting this diet plan');
    }

    if (age > 50) {
      considerations.push('Pay special attention to calcium and vitamin D intake for bone health');
      considerations.push('Consider consulting with a healthcare provider about age-specific nutritional needs');
    }

    // Create the diet plan object
    const aiDietPlan: DietPlanResponse = {
      dailyMeals: {
        breakfast,
        lunch,
        dinner,
        snacks
      },
      nutritionInfo: {
        calories: adjustedCalories + ' calories per day',
        protein: proteinPercentage,
        carbs: carbsPercentage,
        fats: fatsPercentage
      },
      recommendations: [
        'Drink at least 8 glasses of water daily',
        'Aim for 30 minutes of physical activity most days',
        'Include a variety of colorful fruits and vegetables',
        'Prioritize whole foods over processed foods',
        'Practice mindful eating habits'
      ],
      considerations
    };

    setDietPlanData(aiDietPlan);
    setShowAiInsights(true);
  };

  const resetForm = () => {
    setUserData({
      gender: '',
      age: '',
      height: '',
      weight: '',
      bmi: ''
    });
    setMedicalReport(null);
    setMedicalReportName('');
    setDietPlan(null);
    setDietPlanData(null);
    setShowAiInsights(false);
    setErrors({});
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Advanced Diet Recommendation
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Fill in your details for a personalized diet plan based on AI analysis.
          Upload an optional medical report for a more tailored recommendation.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
            <Box sx={{ p: 1, width: { xs: '100%', sm: '50%', md: '25%' } }}>
              <FormControl fullWidth error={!!errors.gender}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  name="gender"
                  value={userData.gender}
                  label="Gender"
                  onChange={handleChange}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
              </FormControl>
            </Box>

            <Box sx={{ p: 1, width: { xs: '100%', sm: '50%', md: '25%' } }}>
              <TextField
                fullWidth
                label="Age"
                name="age"
                type="number"
                value={userData.age}
                onChange={handleChange}
                error={!!errors.age}
                helperText={errors.age}
              />
            </Box>

            <Box sx={{ p: 1, width: { xs: '100%', sm: '50%', md: '25%' } }}>
              <TextField
                fullWidth
                label="Height (cm)"
                name="height"
                type="number"
                value={userData.height}
                onChange={handleChange}
                error={!!errors.height}
                helperText={errors.height}
              />
            </Box>

            <Box sx={{ p: 1, width: { xs: '100%', sm: '50%', md: '25%' } }}>
              <TextField
                fullWidth
                label="Weight (kg)"
                name="weight"
                type="number"
                value={userData.weight}
                onChange={handleChange}
                error={!!errors.weight}
                helperText={errors.weight}
              />
            </Box>

            <Box sx={{ p: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Calculated BMI"
                  value={userData.bmi}
                  disabled
                  sx={{ width: '150px' }}
                />
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ height: '56px' }}
                >
                  Upload Medical Report (Optional)
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                </Button>
                {medicalReportName && (
                  <Chip
                    label={medicalReportName}
                    onDelete={() => {
                      setMedicalReport(null);
                      setMedicalReportName('');
                    }}
                  />
                )}
              </Box>
              <FormHelperText>
                Medical reports help provide more personalized recommendations
              </FormHelperText>
            </Box>

            <Box sx={{ p: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  minWidth: '150px',
                  minHeight: 48,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Get Diet Plan'}
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={resetForm}
                disabled={loading}
                sx={{
                  minWidth: '150px',
                  minHeight: 48,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  ml: 2,
                }}
              >
                Reset
              </Button>

              </Box>
            </Box>
          </Box>
        </Box>

        {dietPlan && (
          <>
            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Your Personalized Diet Plan
            </Typography>

            {dietPlanData && (
              <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1.5 }}>
                  <Box sx={{ p: 1.5, width: { xs: '100%', md: '50%' } }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Daily Nutrition Targets
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip label={`Calories: ${dietPlanData.nutritionInfo.calories}`} color="primary" />
                      <Chip label={`Protein: ${dietPlanData.nutritionInfo.protein}`} />
                      <Chip label={`Carbs: ${dietPlanData.nutritionInfo.carbs}`} />
                      <Chip label={`Fats: ${dietPlanData.nutritionInfo.fats}`} />
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5, width: { xs: '100%', md: '50%' } }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Health Considerations
                    </Typography>
                    <List dense>
                      {dietPlanData.considerations.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box sx={{ p: 1.5, width: '100%' }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Recommended Meal Plan
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
                      <Box sx={{ p: 1, width: { xs: '50%', md: '25%' } }}>
                        <Typography variant="subtitle2" color="primary">Breakfast Options</Typography>
                        <List dense>
                          {dietPlanData.dailyMeals.breakfast.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Box sx={{ p: 1, width: { xs: '50%', md: '25%' } }}>
                        <Typography variant="subtitle2" color="primary">Lunch Options</Typography>
                        <List dense>
                          {dietPlanData.dailyMeals.lunch.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Box sx={{ p: 1, width: { xs: '50%', md: '25%' } }}>
                        <Typography variant="subtitle2" color="primary">Dinner Options</Typography>
                        <List dense>
                          {dietPlanData.dailyMeals.dinner.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Box sx={{ p: 1, width: { xs: '50%', md: '25%' } }}>
                        <Typography variant="subtitle2" color="primary">Healthy Snacks</Typography>
                        <List dense>
                          {dietPlanData.dailyMeals.snacks.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5, width: '100%' }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      General Recommendations
                    </Typography>
                    <List dense>
                      {dietPlanData.recommendations.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              </Paper>
            )}

            <Accordion expanded={showAiInsights} onChange={() => setShowAiInsights(!showAiInsights)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">AI-Generated Detailed Diet Plan</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{dietPlan}</Typography>
              </AccordionDetails>
            </Accordion>

            {medicalReport && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your medical report has been analyzed and incorporated into this diet plan.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DietRecommendation;
